/* eslint-disable */
// ─── Close button ─────────────────────────────────────────────────────────────
// One close control for the whole app, modelled on the macOS window close:
// quiet at rest, decisive red on hover. The glyph is a drawn SVG rather than a
// font "×" so the two strokes meet exactly at the centre and stay optically
// square at any size — a text × sits slightly high and off-centre in most
// faces, which is what makes a hand-rolled one look unfinished.
//
// Variants:
//   default  — light surfaces (modals, drawers, cards)
//   onDark   — coloured or photographic headers
//   bare     — no chrome until hover; for dense toolbars
import { useState } from "react";

const RED       = "#FF453A";   // hover fill
const RED_PRESS = "#D70015";   // active

export default function CloseButton({
  onClick,
  size = 34,
  variant = "default",
  title = "Close",
  style: styleOverride = {},
  ...rest
}) {
  const [hover, setHover] = useState(false);
  const [down, setDown]   = useState(false);

  const onDark = variant === "onDark";
  const bare   = variant === "bare";

  // No disc behind the glyph, in any variant. A filled circle around a cross is
  // chrome the cross does not need, and over a photo or a coloured header it
  // read as a grey blob with something inside it rather than a control.
  //
  // The macOS cue survives, moved onto the mark itself: the × turns red on
  // hover instead of sitting on a red disc.
  const resting =
    onDark ? { fg: "rgba(255,255,255,0.95)" }
    : bare ? { fg: "#9a9aa4" }
    :        { fg: "#6b6b76" };

  const bg   = "transparent";
  const fg   = down ? RED_PRESS : hover ? RED : resting.fg;
  const ring = "transparent";

  // The stroke thickens very slightly on hover so the glyph reads as solid
  // against the red rather than thin and washed out. It also scales with the
  // button: a fixed 1.9 looked fine at 24px but left a large button looking
  // like a wide pale ring drawn around a thin, timid cross.
  const base   = Math.max(2.0, size * 0.068);   // slightly heavier now there is no disc to sit on
  const stroke = hover || down ? base + 0.2 : base;
  // Glyph spans 40% of the button (36% on small ones, which need more padding
  // to stay tappable). It was 36% everywhere, which read as under-sized.
  const inset  = size <= 26 ? 0.32 : 0.30;
  const a = size * inset;
  const b = size * (1 - inset);

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      onMouseEnter={()=>setHover(true)}
      onMouseLeave={()=>{ setHover(false); setDown(false); }}
      onMouseDown={()=>setDown(true)}
      onMouseUp={()=>setDown(false)}
      style={{
        width: size, height: size, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        padding: 0, borderRadius: "50%", cursor: "pointer",
        background: bg,
        border: `1px solid ${ring}`,
        transform: down ? "scale(0.94)" : "scale(1)",
        transition: "background 0.14s ease, border-color 0.14s ease, transform 0.08s ease",
        WebkitTapHighlightColor: "transparent",
        ...styleOverride,
      }}
      {...rest}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true"
        style={{
          display: "block",
          transition: "opacity 0.14s ease",
          // Without a disc, a white cross can disappear into a pale photo. A
          // soft drop shadow keeps it readable on any background and is
          // invisible on a flat surface.
          filter: onDark ? "drop-shadow(0 1px 3px rgba(0,0,0,0.45))" : "none",
        }}>
        <line x1={a} y1={a} x2={b} y2={b} stroke={fg} strokeWidth={stroke} strokeLinecap="round" />
        <line x1={b} y1={a} x2={a} y2={b} stroke={fg} strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    </button>
  );
}
